import { Bundle, Release } from "../../../types";

/**
 * Predicates for `replaceCompletedEntityInCache` — they decide whether a
 * record nested somewhere in a TanStack Query cache value matches the entity
 * we just successfully purchased.
 */

/** Some Release variants surface a `uuid` field at runtime that isn't on the
 *  static type. Read it defensively so the cache-matcher can use it. */
export function getRuntimeReleaseUuid(release: Release | undefined): string | undefined {
  const uuid = (release as { uuid?: unknown } | undefined)?.uuid;
  return typeof uuid === "string" && uuid !== "" ? uuid : undefined;
}

/**
 * PDF release purchase: matches a Release record by `entity_type` + `id`.
 * (PDF flow doesn't carry a uuid runtime field, so id-only is intentional.)
 */
export function isMatchingPdfReleaseEntity(
  value: Record<string, unknown>,
  completedRelease: Release,
): boolean {
  return value.entity_type === "Release" && value.id === completedRelease.id;
}

/**
 * Standard release purchase: matches by `id` OR by uuid when the runtime
 * Release object exposes one (some cache shapes index releases by uuid).
 */
export function isMatchingReleaseEntity(
  value: Record<string, unknown>,
  completedRelease: Release,
): boolean {
  const completedReleaseUuid = getRuntimeReleaseUuid(completedRelease);
  return (
    value.entity_type === "Release" &&
    (value.id === completedRelease.id ||
      (completedReleaseUuid !== undefined &&
        value.uuid === completedReleaseUuid))
  );
}

/**
 * Bundle purchase: matches by uuid OR id, on any entity_type that contains
 * "Bundle" (covers Bundle, ListBundle, ContentBundle, etc.).
 */
export function isMatchingBundleEntity(
  value: Record<string, unknown>,
  completedBundle: Bundle,
): boolean {
  const entityType = value.entity_type;
  const isBundleEntity =
    typeof entityType === "string" && entityType.includes("Bundle");
  return (
    isBundleEntity &&
    (value.uuid === completedBundle.uuid || value.id === completedBundle.id)
  );
}
